// dashboardPestanas.js — Pestañas configurables del dashboard público de programa.
//
// Cada dashboard (proyecto.config_json.dashboards[]) puede traer `pestanas`: qué
// pestañas se ven, en qué orden, con qué título y, en las de Cobertura, por qué
// campos del registro se segmenta (país, especialidad, ciudad…). Sin `pestanas` el
// dashboard se ve como siempre. Lo usan el editor (AdminDashboardBuilder) y la
// página pública (DashboardPublicoPage), para que los dos entiendan lo mismo.

export const TIPOS_PESTANA = {
  resumen:        { titulo: 'Resumen',          ayuda: 'Cifras generales, tendencia de registros y avance.' },
  cobertura:      { titulo: 'Cobertura',        ayuda: 'Volumen y certificación por segmento.', repetible: true },
  journey:        { titulo: 'Journey',          ayuda: 'Dónde está cada estudiante en el recorrido.' },
  satisfaccion:   { titulo: 'Satisfacción',     ayuda: 'Resultados de la encuesta.' },
  estudiantes:    { titulo: 'Estudiantes',      ayuda: 'Tabla de estudiantes con su progreso.' },
  calidad:        { titulo: 'Calidad de datos', ayuda: 'Registros incompletos o inconsistentes.' },
  comunicaciones: { titulo: 'Comunicaciones',   ayuda: 'Correos enviados y sus resultados.' },
  demografico:    { titulo: 'Demografía',       ayuda: 'Quiénes son (país, ciudad…) y cuándo se conectan.' },
  consultas:      { titulo: 'Consultas al asistente', ayuda: 'Qué le preguntan a la IA del curso y sobre qué temas.' },
  evento:         { titulo: 'Certificación del evento', ayuda: 'Inscritos al evento en vivo: quién obtuvo cada certificado y cómo cumplió cada sesión. Requiere el ID del evento.' },
};

// Demografía: por qué campos del registro se describe a los estudiantes.
export const SEGMENTOS_DEMOGRAFIA = ['pais'];

// Lo que mostraba Cobertura antes de ser configurable.
export const SEGMENTOS_POR_DEFECTO = ['pais', 'especialidad', 'convenio'];

// Journey: los paneles de análisis que se pueden ocultar (todos visibles por defecto).
export const PANELES_JOURNEY = [
  { key: 'distribucion', label: 'Distribución por puntuación' },
  { key: 'modulos', label: 'Progreso de contenido por módulo' },
  { key: 'evaluaciones', label: 'Calificaciones de evaluación' },
  { key: 'requisitos', label: 'Requisitos de certificación' },
];
export const normalizarPanelesJourney = (p) => Object.fromEntries(
  PANELES_JOURNEY.map(x => [x.key, !(p && p[x.key] === false)])
);

/**
 * Compromiso de metas: lo que el proyecto se comprometió a inscribir y cómo va.
 *
 * Cada fila es un grupo de valores de un mismo campo (normalmente país, pero sirve
 * cualquier campo del registro) con su meta: así «CADR» puede juntar Guatemala,
 * Costa Rica y los demás en una sola línea, y cada país puede ir solo si hace falta.
 */
export const normalizarMetas = (m) => {
  const campo = (m && typeof m.campo === 'string' && m.campo) || 'pais';
  const filas = Array.isArray(m?.filas) ? m.filas : [];
  return {
    campo,
    titulo: (m && typeof m.titulo === 'string' && m.titulo.trim()) || 'Compromiso de metas',
    filas: filas.map((f, i) => ({
      id: f.id || `meta_${i}`,
      etiqueta: typeof f.etiqueta === 'string' ? f.etiqueta : '',
      valores: Array.isArray(f.valores) ? f.valores.map(String) : [],
      target: Number.isFinite(Number(f.target)) ? Math.max(0, Math.round(Number(f.target))) : 0,
    })),
  };
};

/**
 * Regiones: valores de un campo que el tablero trata como uno solo («CADR» en vez de
 * siete países). El backend las aplica al armar cada estudiante, así que cobertura,
 * demografía, filtros, metas y descargas hablan ya de la región.
 */
export const normalizarRegiones = (r) => ({
  campo: (r && typeof r.campo === 'string' && r.campo) || 'pais',
  grupos: (Array.isArray(r?.grupos) ? r.grupos : []).map((g, i) => ({
    id: g.id || `region_${i}`,
    etiqueta: typeof g.etiqueta === 'string' ? g.etiqueta : '',
    valores: Array.isArray(g.valores) ? g.valores.map(String) : [],
  })),
});

export const pestanasPorDefecto = () => Object.keys(TIPOS_PESTANA).map(tipo => ({
  id: tipo,
  tipo,
  titulo: TIPOS_PESTANA[tipo].titulo,
  visible: true,
  ...(tipo === 'cobertura' ? { segmentos: [...SEGMENTOS_POR_DEFECTO] } : {}),
  ...(tipo === 'demografico' ? { segmentos: [...SEGMENTOS_DEMOGRAFIA] } : {}),
}));

/** Limpia lo guardado: tipos conocidos, ids únicos, títulos y segmentos válidos. */
export const normalizarPestanas = (guardadas) => {
  if (!Array.isArray(guardadas) || !guardadas.length) return pestanasPorDefecto();
  const vistos = new Set();
  const out = [];
  guardadas.forEach((p, i) => {
    if (!p || !TIPOS_PESTANA[p.tipo]) return;
    let id = String(p.id || p.tipo);
    if (vistos.has(id)) id = `${p.tipo}_${i}`;
    vistos.add(id);
    out.push({
      id,
      tipo: p.tipo,
      titulo: (p.titulo || '').trim() || TIPOS_PESTANA[p.tipo].titulo,
      visible: p.visible !== false,
      ...(p.tipo === 'cobertura'
        ? {
          segmentos: Array.isArray(p.segmentos) && p.segmentos.length ? p.segmentos.filter(Boolean) : [...SEGMENTOS_POR_DEFECTO],
          bloques: normalizarBloques(p.bloques),
        }
        : {}),
      // Journey: campos con panel de registrados y certificados (ninguno por defecto)
      // y qué paneles de análisis se ven.
      ...(p.tipo === 'demografico'
        ? { segmentos: Array.isArray(p.segmentos) && p.segmentos.length ? p.segmentos.filter(Boolean) : [...SEGMENTOS_DEMOGRAFIA] }
        : {}),
      ...(p.tipo === 'journey'
        ? {
          segmentos: Array.isArray(p.segmentos) ? p.segmentos.filter(Boolean) : [],
          paneles: normalizarPanelesJourney(p.paneles),
        }
        : {}),
    });
  });
  // Una pestaña única que faltara (dashboards guardados antes de agregarla) se añade
  // al final visible, para no esconder funciones nuevas por accidente.
  Object.keys(TIPOS_PESTANA).forEach(tipo => {
    if (TIPOS_PESTANA[tipo].repetible || out.some(p => p.tipo === tipo)) return;
    out.push({
      id: tipo, tipo, titulo: TIPOS_PESTANA[tipo].titulo, visible: true,
      ...(tipo === 'demografico' ? { segmentos: [...SEGMENTOS_DEMOGRAFIA] } : {}),
    });
  });
  return out.length ? out : pestanasPorDefecto();
};

// ── Bloques adicionales de una pestaña de cobertura ──────────────────────────
// Todos trabajan sobre el campo principal de la pestaña (el primer segmento):
//   resumen      tarjetas arriba que agrupan valores («Asignados», «Sin asignar»…)
//   indicadores  estudiantes, con avance, avance promedio y certificados, pudiendo
//                dejar fuera algunos valores (p. ej. «Sin visitador»)
//   contacto     lista priorizada por días de inactividad, ordenable y descargable
export const bloquesVacios = () => ({
  resumen: { activo: false, tarjetas: [] },
  // excluir = valores que NO cuentan como asignados (p. ej. «No Panel»). La tarjeta
  // «Asignados» sale si hay alguno; solo_asignados decide si avance y certificados se
  // calculan sobre los asignados o sobre todos.
  indicadores: { activo: false, titulo: 'Indicadores de seguimiento', excluir: [], solo_asignados: false },
  contacto: { activo: false, titulo: 'Lista priorizada de contacto', ubicacion: '', excluir: [] },
});

const listaTexto = (v) => (Array.isArray(v) ? v.map(x => String(x).trim()).filter(Boolean) : []);

export const normalizarBloques = (b) => {
  const base = bloquesVacios();
  if (!b || typeof b !== 'object') return base;
  return {
    resumen: {
      activo: !!b.resumen?.activo,
      tarjetas: (Array.isArray(b.resumen?.tarjetas) ? b.resumen.tarjetas : []).map(t => ({
        titulo: String(t?.titulo || '').trim() || 'Grupo',
        descripcion: String(t?.descripcion || '').trim(),
        valores: listaTexto(t?.valores),
        resto: !!t?.resto,
      })),
    },
    indicadores: {
      activo: !!b.indicadores?.activo,
      titulo: String(b.indicadores?.titulo || '').trim() || base.indicadores.titulo,
      excluir: listaTexto(b.indicadores?.excluir),
      solo_asignados: !!b.indicadores?.solo_asignados,
    },
    contacto: {
      activo: !!b.contacto?.activo,
      titulo: String(b.contacto?.titulo || '').trim() || base.contacto.titulo,
      ubicacion: String(b.contacto?.ubicacion || ''),
      excluir: listaTexto(b.contacto?.excluir),
    },
  };
};

/** Las opciones cargadas de un campo de lista del registro (vacío si es texto libre). */
export const opcionesDeCampo = (registroConfig = {}, campo = '') => {
  const lista = Array.isArray(registroConfig?.campos)
    ? registroConfig.campos
    : Object.entries(registroConfig?.campos || {}).map(([key, c]) => ({ key, ...(c || {}) }));
  const c = lista.find(x => x?.key === campo);
  return Array.isArray(c?.opciones) ? c.opciones.map(String) : [];
};

/** Clave para comparar valores sin tildes, mayúsculas ni espacios de más. */
export const claveSegmento = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();

export const SIN_DATO = 'Sin dato';

/**
 * Etiqueta de un campo como texto. En proyectos multi-idioma (Uroacademy) viene como
 * {es, pt}: se usa el español o el primer idioma que haya.
 */
export const textoEtiqueta = (e) => {
  if (e === null || e === undefined) return '';
  if (typeof e === 'string') return e;
  if (typeof e === 'object') return String(e.es || Object.values(e).find(v => typeof v === 'string' && v) || '');
  return String(e);
};

// Campos que traen todos los estudiantes en el nivel superior del payload.
const CAMPOS_DIRECTOS = {
  pais: 'País',
  especialidad: 'Especialidad',
  convenio: 'Convenio o institución',
  subrol: 'Perfil',
};

// Campos del registro que no tienen sentido como segmento (valores únicos por persona).
const NO_SEGMENTABLES = new Set(['nombre', 'apellido', 'email', 'dni', 'telefono', 'password', 'terminos']);

/**
 * Campos por los que se puede segmentar en un proyecto: los directos y los del
 * formulario de registro (incluidos los personalizados, p. ej. `cf_ciudad`).
 * `campos` = registro_config.campos del proyecto; `etiquetasCortas` recorta
 * etiquetas largas tipo pregunta.
 */
export const camposSegmentables = (registroConfig = {}, { tieneConvenios = true, tieneSubroles = true } = {}) => {
  const lista = Array.isArray(registroConfig?.campos)
    ? registroConfig.campos
    : Object.entries(registroConfig?.campos || {}).map(([key, c]) => ({ key, ...(c || {}) }));
  const out = [];
  const agregar = (key, label) => { if (!out.some(c => c.key === key)) out.push({ key, label }); };
  agregar('pais', CAMPOS_DIRECTOS.pais);
  agregar('especialidad', CAMPOS_DIRECTOS.especialidad);
  if (tieneConvenios) agregar('convenio', CAMPOS_DIRECTOS.convenio);
  if (tieneSubroles) agregar('subrol', CAMPOS_DIRECTOS.subrol);
  lista.forEach(c => {
    if (!c?.key || NO_SEGMENTABLES.has(c.key) || c.visible === false) return;
    if (CAMPOS_DIRECTOS[c.key]) return;
    const etq = textoEtiqueta(c.etiqueta).trim();
    agregar(c.key, etq && etq.length <= 40 ? etq : (etq.slice(0, 40) || c.key.replace(/^cf_/, '').replace(/_/g, ' ')));
  });
  return out;
};

/** Valor de un estudiante para un segmento (directo o del perfil de registro). */
export const valorSegmento = (e, campo) => {
  const v = CAMPOS_DIRECTOS[campo] ? e[campo] : e.perfil?.[campo];
  if (Array.isArray(v)) return v.join(', ');
  return v === null || v === undefined || v === '' ? null : String(v);
};

export const etiquetaSegmento = (campo, perfilMeta = []) =>
  CAMPOS_DIRECTOS[campo] || textoEtiqueta(perfilMeta.find(c => c.key === campo)?.etiqueta) || campo.replace(/^cf_/, '').replace(/_/g, ' ');
